import { QueryFunction, useQuery } from "@tanstack/react-query";
import { accrualReviewContract } from "@common/contracts/accrual-review.contract";
import {
    ApproveAccrualReviewBody,
    AccrualReviewApproveResponse,
    AccrualReviewUndoResponse,
    BulkApproveAccrualReviewBody,
    BulkApproveAccrualReviewResponse,
    BulkUnmatchInvoicesResponse,
    HasMatchableDocumentsResponse,
    ReverseApproveAccrualReviewBody,
} from "@common/schemas/domains/accrualReview/accrual-review.schema";
import { useActiveWorkspace } from "@hooks/useActiveWorkspace";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { queryClient } from "../queryClient";

const accrualReviewClient = getBackendAPIClient(accrualReviewContract);

// Matches the workspace-records probes in invoice.api.ts/bill.api.ts: this
// only checks existence, which changes rarely, so a short stale window keeps
// it from re-firing (and flashing tab options) as the match drawer re-opens.
const HAS_MATCHABLE_DOCUMENTS_STALE_TIME = 60_000;

const HAS_MATCHABLE_DOCUMENTS_QUERY_KEY = "hasMatchableDocuments";

type HasMatchableDocumentsQueryKey = [
    typeof HAS_MATCHABLE_DOCUMENTS_QUERY_KEY,
    string | undefined,
];

const fetchHasMatchableDocuments: QueryFunction<
    HasMatchableDocumentsResponse | undefined,
    HasMatchableDocumentsQueryKey
> = async ({ queryKey }) => {
    const [, workspaceId] = queryKey;
    if (!workspaceId) {
        return;
    }
    const response = await accrualReviewClient.hasMatchableDocuments({
        params: { workspaceId },
    });
    return response.body;
};

export function useHasMatchableDocuments(enabled: boolean) {
    const activeWorkspace = useActiveWorkspace();
    const workspaceId = activeWorkspace?.id;

    const { data } = useQuery({
        queryKey: [HAS_MATCHABLE_DOCUMENTS_QUERY_KEY, workspaceId],
        queryFn: fetchHasMatchableDocuments,
        enabled: enabled && !!workspaceId,
        staleTime: HAS_MATCHABLE_DOCUMENTS_STALE_TIME,
    });

    // Only a completed check that reports zero documents should hide a tab.
    // While the probe is in flight — and if it fails outright — stay permissive
    // (assume both exist) rather than treating the missing answer as
    // confirmation there are no documents. A failed or pending check must never
    // remove a tab whose documents may well exist; a successful `false` is the
    // only thing that narrows the tabs. See the tab-gating rationale in
    // `AccrualMatchPage`.
    return {
        hasInvoices: data?.hasInvoices ?? true,
        hasBills: data?.hasBills ?? true,
    };
}

// Invoice/bill creation happens on separate query keys, so a cached
// existence probe would keep hiding a newly-created document's tab until it
// goes stale. Creation paths call this to drop the cached answer immediately.
export function invalidateHasMatchableDocumentsQuery() {
    queryClient.invalidateQueries({
        queryKey: [HAS_MATCHABLE_DOCUMENTS_QUERY_KEY],
    });
}

export async function approveAccrualReview({
    workspaceId,
    body,
    extraHeaders,
}: {
    workspaceId: string;
    body: ApproveAccrualReviewBody;
    extraHeaders?: Record<string, string>;
}): Promise<AccrualReviewApproveResponse> {
    const response = await accrualReviewClient.approve({
        params: { workspaceId },
        body,
        extraHeaders,
    });

    return response.body;
}

export async function reverseApproveAccrualReview({
    workspaceId,
    body,
    extraHeaders,
}: {
    workspaceId: string;
    body: ReverseApproveAccrualReviewBody;
    extraHeaders?: Record<string, string>;
}): Promise<AccrualReviewApproveResponse> {
    const response = await accrualReviewClient.reverseApprove({
        params: { workspaceId },
        body,
        extraHeaders,
    });

    return response.body;
}

export async function bulkApproveAccrualReview({
    workspaceId,
    body,
}: {
    workspaceId: string;
    body: BulkApproveAccrualReviewBody;
}): Promise<BulkApproveAccrualReviewResponse> {
    const response = await accrualReviewClient.bulkApprove({
        params: { workspaceId },
        body,
    });

    return response.body;
}

export async function undoAccrualReview({
    workspaceId,
    transactionId,
    revertedSourceId,
}: {
    workspaceId: string;
    transactionId: number;
    revertedSourceId?: string;
}): Promise<AccrualReviewUndoResponse> {
    const response = await accrualReviewClient.undo({
        params: { workspaceId },
        body: { transactionId, revertedSourceId },
    });

    return response.body;
}

export async function bulkUnmatchInvoices({
    workspaceId,
    transactionIds,
}: {
    workspaceId: string;
    transactionIds: number[];
}): Promise<BulkUnmatchInvoicesResponse> {
    const response = await accrualReviewClient.bulkUnmatchInvoices({
        params: { workspaceId },
        body: { transactionIds },
    });

    return response.body;
}
